import type { EditTarget, LayoutBlock } from '@web-studio/shared-schemas';
import type { MatrixLocale } from '@web-studio/core-fixtures';
import { useEffect, useRef, useState } from 'react';

type InspectorTab = 'fields' | 'json';

interface FixtureInspectorProps {
  block: LayoutBlock | undefined;
  blocks?: LayoutBlock[];
  selectedBlockId?: string | null;
  onSelectBlock?: (blockId: string) => void;
  locale: MatrixLocale;
  engineerMode: boolean;
  activeEditTarget?: EditTarget | null;
  onContentChange: (fieldKey: string, locale: string, value: string) => void;
  onMediaChange: (fieldKey: string, value: string) => void;
}

function fieldElementId(kind: 'content' | 'media', fieldKey: string): string {
  return kind === 'content' ? `lab-edit-field-${fieldKey}` : `lab-edit-media-${fieldKey}`;
}

function resolveInspectorFieldId(target: EditTarget): string | null {
  switch (target.kind) {
    case 'content-field':
      return fieldElementId('content', target.fieldKey);
    case 'media-field':
      return fieldElementId('media', target.fieldKey);
    case 'menu-item':
    case 'menu-column':
      return fieldElementId('content', 'menuData');
    default: {
      const _exhaustive: never = target;
      return _exhaustive;
    }
  }
}

export function FixtureInspector({
  block,
  blocks = [],
  selectedBlockId,
  onSelectBlock,
  locale,
  engineerMode,
  activeEditTarget = null,
  onContentChange,
  onMediaChange,
}: FixtureInspectorProps) {
  const rootRef = useRef<HTMLElement>(null);
  const [tab, setTab] = useState<InspectorTab>('fields');

  useEffect(() => {
    if (!engineerMode || !activeEditTarget) return;
    setTab('fields');
  }, [engineerMode, activeEditTarget]);

  useEffect(() => {
    if (!engineerMode || !activeEditTarget || !rootRef.current || tab !== 'fields') return;
    const id = resolveInspectorFieldId(activeEditTarget);
    if (!id) return;
    const container = rootRef.current.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
    if (!container) return;
    container.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    const control = container.querySelector<HTMLInputElement | HTMLTextAreaElement>(
      'input, textarea',
    );
    control?.focus({ preventScroll: true });
  }, [engineerMode, activeEditTarget, block?.blockId, tab]);

  if (!block) {
    return (
      <aside className="lab-inspector">
        <p className="text-sm" style={{ color: 'var(--lab-muted)' }}>
          No block loaded.
        </p>
      </aside>
    );
  }

  const contentKeys = Object.keys(block.content).sort();
  const mediaKeys = Object.keys(block.media).sort();
  const activeFieldId = activeEditTarget ? resolveInspectorFieldId(activeEditTarget) : null;
  const showJsonTab = engineerMode;

  return (
    <aside ref={rootRef} className="lab-inspector">
      <div className="lab-inspector__head">
        <div className="lab-inspector__tabs" role="tablist" aria-label="Inspector panels">
          <button
            type="button"
            role="tab"
            id="lab-inspector-tab-fields"
            aria-selected={tab === 'fields'}
            aria-controls="lab-inspector-panel-fields"
            className={`lab-inspector__tab${tab === 'fields' ? ' lab-inspector__tab--active' : ''}`}
            onClick={() => setTab('fields')}
          >
            Content fields
          </button>
          {showJsonTab ? (
            <button
              type="button"
              role="tab"
              id="lab-inspector-tab-json"
              aria-selected={tab === 'json'}
              aria-controls="lab-inspector-panel-json"
              className={`lab-inspector__tab${tab === 'json' ? ' lab-inspector__tab--active' : ''}`}
              onClick={() => setTab('json')}
            >
              Raw JSON
            </button>
          ) : null}
        </div>
      </div>

      {tab === 'fields' ? (
        <div
          id="lab-inspector-panel-fields"
          role="tabpanel"
          aria-labelledby="lab-inspector-tab-fields"
          className="lab-inspector__panel"
        >
          {blocks.length > 1 && onSelectBlock ? (
            <div className="lab-field">
              <label htmlFor="lab-inspect-block">Inspect block</label>
              <select
                id="lab-inspect-block"
                value={selectedBlockId ?? block.blockId}
                onChange={(e) => onSelectBlock(e.target.value)}
              >
                {blocks.map((b) => (
                  <option key={b.blockId} value={b.blockId}>
                    {b.componentType}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <section className="lab-inspector__section lab-inspector__section--flush">
            {contentKeys.length === 0 ? (
              <p className="text-xs" style={{ color: 'var(--lab-muted)' }}>
                No content keys
              </p>
            ) : (
              contentKeys.map((key) => {
                const value = block.content[key]?.[locale] ?? '';
                const isJson = key === 'menuData' || value.startsWith('{');
                const fieldId = fieldElementId('content', key);
                const isActive = activeFieldId === fieldId;
                return (
                  <label
                    key={key}
                    id={fieldId}
                    className={`lab-inspector__field${isActive ? ' lab-inspector__field--active' : ''}`}
                  >
                    <span>{key}</span>
                    <textarea
                      rows={isJson ? 8 : engineerMode ? 3 : 2}
                      value={value}
                      onChange={(e) => onContentChange(key, locale, e.target.value)}
                    />
                  </label>
                );
              })
            )}
          </section>

          {mediaKeys.length > 0 ? (
            <section className="lab-inspector__section">
              <h3 className="lab-inspector__title">Media</h3>
              {mediaKeys.map((key) => {
                const fieldId = fieldElementId('media', key);
                const isActive = activeFieldId === fieldId;
                return (
                  <label
                    key={key}
                    id={fieldId}
                    className={`lab-inspector__field${isActive ? ' lab-inspector__field--active' : ''}`}
                  >
                    <span>{key}</span>
                    <input
                      type="url"
                      value={block.media[key] ?? ''}
                      onChange={(e) => onMediaChange(key, e.target.value)}
                    />
                  </label>
                );
              })}
            </section>
          ) : null}
        </div>
      ) : (
        <div
          id="lab-inspector-panel-json"
          role="tabpanel"
          aria-labelledby="lab-inspector-tab-json"
          className="lab-inspector__panel"
        >
          <pre className="lab-inspector__json lab-inspector__json--full">
            {JSON.stringify(block, null, 2)}
          </pre>
        </div>
      )}
    </aside>
  );
}
